'use client';

import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal, .reveal-scale');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  span?: 'wide' | 'tall' | 'normal';
}

const images: GalleryImage[] = [
  {
    id: 1,
    src: '/assets/images/diggity-3.jpg',
    alt: 'Diggity Glover performing live in front of the Dystopian Sun banner',
    span: 'wide',
  },
  {
    id: 2,
    src: '/assets/images/drum-1.jpg',
    alt: 'Full drum kit on stage before a live performance, black and white',
    span: 'normal',
  },
  {
    id: 3,
    src: '/assets/images/studio-1.jpg',
    alt: 'Mixing desk and studio monitors in a darkened control room',
    span: 'normal',
  },
  {
    id: 4,
    src: '/assets/images/diggity-15.jpg',
    alt: 'Portrait of Diggity Glover performing under stage lighting',
    span: 'tall',
  },
  {
    id: 5,
    src: '/assets/images/mixing-1.jpg',
    alt: 'Full mix session in the DAW with stems and automation',
    span: 'normal',
  },
  {
    id: 6,
    src: '/assets/images/diggity-8.jpg',
    alt: 'Diggity Glover mid-performance behind the kit, side profile',
    span: 'normal',
  },
  {
    id: 7,
    src: '/assets/images/drum-10.jpg',
    alt: 'Top-down view of the drum kit during a session',
    span: 'normal',
  },
  {
    id: 8,
    src: '/assets/images/diggity-5.jpg',
    alt: 'Diggity Glover drumming live on stage, high-contrast black and white',
    span: 'wide',
  },
  {
    id: 9,
    src: '/assets/images/drum-9.jpg',
    alt: 'Drum kit framed vertically in the live room, black and white',
    span: 'tall',
  },
  {
    id: 10,
    src: '/assets/images/mixing-3.jpg',
    alt: 'Mixing session open in the DAW, multitrack arrangement',
    span: 'normal',
  },
  {
    id: 11,
    src: '/assets/images/diggity-2.jpg',
    alt: 'Diggity Glover playing a live set, intense drumming shot',
    span: 'normal',
  },
  {
    id: 12,
    src: '/assets/images/studio-main.jpg',
    alt: 'Recording studio with mixing desk, monitors and electronic drum kit',
    span: 'wide',
  },
  {
    id: 13,
    src: '/assets/images/diggity-14.jpg',
    alt: 'Portrait of Diggity Glover drumming, dramatic spotlight',
    span: 'tall',
  },
  {
    id: 14,
    src: '/assets/images/drum-7.jpg',
    alt: 'Studio drum kit setup with cymbals and microphones, black and white',
    span: 'normal',
  },
  {
    id: 15,
    src: '/assets/images/diggity-6.jpg',
    alt: 'Diggity Glover behind the kit during a live performance',
    span: 'normal',
  },
  {
    id: 16,
    src: '/assets/images/drum-12.jpg',
    alt: 'Detailed drum kit and cymbal setup, overhead angle',
    span: 'wide',
  },
];

export default function GalleryPage() {
  useScrollReveal();

  return (
    <div
      style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', overflowX: 'hidden' }}
    >
      <Header />

      {/* ─── PAGE HEADER ─── */}
      <section style={{ padding: '60px 24px 48px', borderBottom: '1px solid #ff1a1a' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <p
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#ff1a1a',
              marginBottom: '16px',
            }}
          >
            // Gallery
          </p>
          <h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              lineHeight: '0.95',
              color: '#ffffff',
              textShadow: '0 0 20px rgba(255,26,26,0.7)',
            }}
          >
            Photo Gallery
          </h1>
        </div>
      </section>

      {/* ─── GALLERY GRID ─── */}
      <section style={{ padding: '60px 24px 80px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '12px',
            }}
          >
            {images.map((img, i) => (
              <div
                key={img.id}
                className={`reveal-scale stagger-${(i % 6) + 1}`}
                style={{
                  border: '1px solid #ff1a1a',
                  overflow: 'hidden',
                  position: 'relative',
                  aspectRatio: img.span === 'wide' ? '16/9' : img.span === 'tall' ? '3/4' : '1/1',
                  background: '#1a1a1a',
                  gridColumn: img.span === 'wide' ? 'span 2' : 'span 1',
                  transition: 'box-shadow 0.3s ease',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow =
                    '0 0 20px rgba(255,26,26,0.6), 0 0 40px rgba(255,26,26,0.2)';
                  const img = e.currentTarget.querySelector('img') as HTMLImageElement;
                  if (img) {
                    img.style.filter = 'grayscale(0%) contrast(1.1) brightness(0.8)';
                    img.style.transform = 'scale(1.04)';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  const img = e.currentTarget.querySelector('img') as HTMLImageElement;
                  if (img) {
                    img.style.filter = 'grayscale(10%) contrast(1.05)';
                    img.style.transform = 'scale(1)';
                  }
                }}
              >
                <AppImage
                  src={img.src}
                  alt={img.alt}
                  fill
                  className="object-cover"
                  style={{
                    filter: 'grayscale(10%) contrast(1.05)',
                    transition: 'transform 0.5s ease, filter 0.4s ease',
                  }}
                />
                {/* Red border glow overlay on hover */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    border: '2px solid transparent',
                    transition: 'border-color 0.3s ease',
                    pointerEvents: 'none',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
